import {
  LRS_VERB_CONSUMED, LRS_VERB_STARTED, OBJECT_TYPE_COURSE, OBJECT_TYPE_MODULE, LRS_APPID_LRNO,
  LRS_VERB_COMPLETED, OBJECT_TYPE_UNIT, OBJECT_TYPE_PROBLEM, LRS_VERB_VIEWED, OBJECT_TYPE_COLLECTION,
} from './constants';
import { sendUserData, setLearningRecords } from '../store/userSlice';
import {
  generateLRSContext, generateLRSID, generateURN, unitHasCompleteRecord,
} from './utilities';
import logger from './logger';

export const sendConsumed = ({
  dispatch, unitId, curatedData, userDetails, env, lrsId, type, problemId, appVersion, tutorialData, tutorialId,
}) => {
  const newLearningRecords = {};
  newLearningRecords[lrsId] = {
    urn: generateURN({
      uid: userDetails.userid, verb: LRS_VERB_CONSUMED, lrsId, type,
    }),
    statements: [
      {
        context: generateLRSContext({
          curatedData, tutorialData, unitId, env, tutorialId,
        }),
        verb: LRS_VERB_CONSUMED,
        timestamp: new Date().toUTCString(),
        objectType: type,
        application: LRS_APPID_LRNO,
      },
    ],
  };

  dispatch(setLearningRecords({
    learningRecords: newLearningRecords,
  }));
  dispatch(sendUserData({
    uid: userDetails.userid, verb: LRS_VERB_CONSUMED, curatedData, env, unitId, type, problemId, appVersion, tutorialData, tutorialId,
  }));
};

export const consumeObject = ({
  dispatch, curatedData, userDetails, env, appVersion, tutorialData, tutorialId,
}) => {
  let lrsId;
  let type;

  if (tutorialData && tutorialData.id) {
    lrsId = generateLRSID({ id: tutorialData.id, env });
    type = OBJECT_TYPE_MODULE;
  } else if (curatedData && tutorialId) {
    lrsId = generateLRSID({ id: tutorialId, env });
    type = OBJECT_TYPE_MODULE;
  } else if (curatedData && curatedData.id) {
    lrsId = generateLRSID({ id: curatedData.id, env });
    type = OBJECT_TYPE_COURSE;
  }

  if (lrsId) {
    sendConsumed({
      dispatch, curatedData, userDetails, env, lrsId, type, appVersion, tutorialData, tutorialId,
    });
  } else {
    logger.error('Failed sending consumeObject.  No data or data without id prop.');
  }
};

export const sendViewed = ({
  dispatch, collectionId, userDetails, env, lrsId, appVersion,
}) => {
  const newLearningRecords = {};
  newLearningRecords[lrsId] = {
    urn: generateURN({
      uid: userDetails.userid, verb: LRS_VERB_VIEWED, lrsId, type: OBJECT_TYPE_COLLECTION,
    }),
    statements: [
      {
        verb: LRS_VERB_STARTED,
        timestamp: new Date().toUTCString(),
        objectType: OBJECT_TYPE_COLLECTION,
        application: LRS_APPID_LRNO,
      },
    ],
  };

  dispatch(setLearningRecords({
    learningRecords: newLearningRecords,
  }));
  dispatch(sendUserData({
    uid: userDetails.userid, verb: LRS_VERB_VIEWED, env, collectionId, type: OBJECT_TYPE_COLLECTION, appVersion,
  }));
};

export const sendStarted = ({
  dispatch, unitId, curatedData, userDetails, env, lrsId, type, problemId, appVersion, tutorialData, tutorialId,
}) => {
  const newLearningRecords = {};
  newLearningRecords[lrsId] = {
    urn: generateURN({
      uid: userDetails.userid, verb: LRS_VERB_STARTED, lrsId, type,
    }),
    statements: [
      {
        context: generateLRSContext({
          curatedData, tutorialData, unitId, env, tutorialId,
        }),
        verb: LRS_VERB_STARTED,
        timestamp: new Date().toUTCString(),
        objectType: type,
        application: LRS_APPID_LRNO,
      },
    ],
  };

  dispatch(setLearningRecords({
    learningRecords: newLearningRecords,
  }));
  dispatch(sendUserData({
    uid: userDetails.userid, verb: LRS_VERB_STARTED, curatedData, env, unitId, type, problemId, appVersion, tutorialData, tutorialId,
  }));
};

export const startObject = ({
  dispatch, curatedData, userDetails, env, appVersion, tutorialData, tutorialId,
}) => {
  let lrsId;
  let type;

  if (tutorialData && tutorialData.id) {
    lrsId = generateLRSID({ id: tutorialData.id, env });
    type = OBJECT_TYPE_MODULE;
  } else if (curatedData && tutorialId) {
    lrsId = generateLRSID({ id: tutorialId, env });
    type = OBJECT_TYPE_MODULE;
  } else if (curatedData && curatedData.id) {
    lrsId = generateLRSID({ id: curatedData.id, env });
    type = OBJECT_TYPE_COURSE;
  }

  if (lrsId) {
    sendStarted({
      dispatch, curatedData, userDetails, env, lrsId, type, appVersion, tutorialData, tutorialId,
    });
  } else {
    logger.error('Failed sending sendStarted.  No data or data without id prop.');
  }
};

const hasRecordOfType = ({
  learningRecords, id, env, verb,
}) => {
  if (id) {
    const objectId = generateLRSID({ id, env });
    if (learningRecords && learningRecords[objectId] && learningRecords[objectId].statements) { // If learning record for this object exists
      const startedStatements = learningRecords[objectId].statements.filter((statement) => statement.verb === verb);
      if (startedStatements.length) { // Found a matching statement
        return true;
      }
    }
  }
  return false;
};

export const getMostRecentStatementOfType = ({
  learningRecords, id, env, verb,
}) => {
  if (id) {
    const objectId = generateLRSID({ id, env });
    if (learningRecords && learningRecords[objectId] && learningRecords[objectId].statements) { // If learning record for this object exists
      const statements = learningRecords[objectId].statements.filter((statement) => statement.verb === verb);
      if (statements.length) { // Found a matching statement
        statements.sort((a, b) => {
          if (new Date(a?.timestamp) > new Date(b?.timestamp)) {
            return -1;
          }
          if (new Date(a?.timestamp) < new Date(b?.timestamp)) {
            return 1;
          }
          return 0;
        });

        return statements[0];
      }
    }
  }
  return {};
};

// Validate if object has a started LRS record
export const hasStartedRecord = ({ learningRecords, id, env }) => hasRecordOfType({
  learningRecords, id, env, verb: LRS_VERB_STARTED,
});

// Validate if object has a consumed LRS record
export const hasConsumedRecord = ({ learningRecords, id, env }) => hasRecordOfType({
  learningRecords, id, env, verb: LRS_VERB_CONSUMED,
});

export const hasCompletedRecord = ({ learningRecords, id, env }) => hasRecordOfType({
  learningRecords, id, env, verb: LRS_VERB_COMPLETED,
});

export const hasViewedRecord = ({ learningRecords, id, env }) => hasRecordOfType({
  learningRecords, id, env, verb: LRS_VERB_VIEWED,
});

export const generateCuratedListInfo = ({
  curatedData, learningRecords, env, status, eventName,
}) => {
  let curatedListInfo;
  let values;
  if (curatedData && Object.keys(curatedData).length) {
    const { id, title } = curatedData;
    curatedListInfo = {
      id,
      name: title,
    };
  }
  if (learningRecords && env) {
    const isCuratedListCompleted = hasConsumedRecord({ learningRecords, id: curatedData.id, env });
    curatedListInfo.alreadyCompleted = isCuratedListCompleted ? 'true' : 'false';
  }
  if (eventName) { // tutorial-consumption
    values = {
      curatedListInfo: {
        status,
      },
    };
    curatedListInfo = { ...{ curatedListInfo }, ...{ values } };
    return curatedListInfo;
  }

  return { curatedListInfo };
};

export const generateTutorialInfo = ({
  tutorialData, learningRecords, env, eventName,
}) => {
  let tutorialInfo;
  let values;

  if (tutorialData && Object.keys(tutorialData).length) {
    const { id, title, isStandAlone } = tutorialData;
    tutorialInfo = {
      id,
      name: title,
      type: isStandAlone ? 'standalone' : 'curated',
    };

    if (learningRecords && env) {
      const isTutorialCompleted = hasConsumedRecord({ learningRecords, id: tutorialData.id, env });
      tutorialInfo.alreadyCompleted = isTutorialCompleted ? 'true' : 'false';
    }

    if (tutorialData.dataSets && tutorialData.dataSets.length && !eventName) {
      values = {
        contentContainer: [{
          containerType: 'learning',
          containerName: 'tutorial resources',
        }],
      };
      tutorialInfo = { ...{ tutorialInfo }, ...{ values } };
      return tutorialInfo;
    }
    if (eventName) { // tutorial-consumption
      values = {
        curatedListInfo: {
          status: 'na',
        },
      };
      tutorialInfo = { ...{ tutorialInfo }, ...{ values } };
      return tutorialInfo;
    }
  }
  return { tutorialInfo };
};

export const sendCompleted = ({
  dispatch, unitId, curatedData, userDetails, env, lrsId, type, problemId, appVersion, tutorialData,
}) => {
  const newLearningRecords = {};
  newLearningRecords[lrsId] = {
    urn: generateURN({
      uid: userDetails.userid, verb: LRS_VERB_COMPLETED, lrsId, type,
    }),
    statements: [
      {
        context: generateLRSContext({
          curatedData, tutorialData, unitId, env,
        }),
        verb: LRS_VERB_COMPLETED,
        timestamp: new Date().toUTCString(),
        objectType: type,
      },
    ],
  };

  dispatch(setLearningRecords({
    learningRecords: newLearningRecords,
  }));
  dispatch(sendUserData({
    uid: userDetails.userid, verb: LRS_VERB_COMPLETED, curatedData, env, unitId, type, problemId, appVersion, tutorialData,
  }));
};

export const completeUnit = ({
  dispatch, unitId, curatedData, userDetails, env, appVersion, tutorialData,
}) => {
  if ((curatedData || tutorialData) && unitId) {
    const lrsId = generateLRSID({ id: unitId, env });

    sendCompleted({
      dispatch, unitId, curatedData, userDetails, env, lrsId, type: OBJECT_TYPE_UNIT, appVersion, tutorialData,
    });
  } else {
    logger.error('Failed sending completeUnit.  No course outline or unitId missing.');
  }
};

export const completeProblem = ({
  dispatch, unitId, curatedData, userDetails, env, problemId, appVersion, tutorialData,
}) => {
  if ((curatedData || tutorialData) && problemId) {
    const lrsId = generateLRSID({ id: problemId, env });

    sendCompleted({
      dispatch, unitId, curatedData, tutorialData, userDetails, env, lrsId, type: OBJECT_TYPE_PROBLEM, problemId, appVersion,
    });
  } else {
    logger.error('Failed sending completeProblem.  No course/module outline or problemId missing.');
  }
};

export const optionallySendModuleEvent = ({
  dispatch, courseOutline, learningRecords, moduleId, userDetails, env, unitId, appVersion, moduleOutline,
}) => {
  const updatedLearningRecs = {};
  const lrsId = generateLRSID({ id: moduleId, env });
  const module = courseOutline ? courseOutline.tutorials?.find((mod) => mod.id === moduleId) : moduleOutline;

  // Optionally Trigger Start event
  if (!hasStartedRecord({ id: moduleId, env, learningRecords })) {
    // Trigger module AA event
    // triggerModulesStatusChange({ courseOutline, status: AA_EVENT_START, env });

    updatedLearningRecs[lrsId] = {
      urn: generateURN({
        uid: userDetails.userid, verb: LRS_VERB_STARTED, lrsId, type: OBJECT_TYPE_MODULE,
      }),
      statements: [
        {
          context: generateLRSContext({
            curatedData: courseOutline, env, tutorialId: moduleId, tutorialData: moduleOutline,
          }),
          verb: LRS_VERB_STARTED,
          timestamp: new Date().toUTCString(),
          objectType: OBJECT_TYPE_MODULE,
        },
      ],
    };

    logger.info(`Dispatching Module Start for: ${moduleId}`);

    dispatch(setLearningRecords({ learningRecords: updatedLearningRecs }));
    dispatch(sendUserData({
      uid: userDetails.userid, verb: LRS_VERB_STARTED, curatedData: courseOutline, env, tutorialId: moduleId, type: OBJECT_TYPE_MODULE, appVersion, tutorialData: moduleOutline,
    }));
  }

  // Optionally Trigger Completed Event
  const reducer = (accumulator, currentUnit) => {
    if (accumulator
        && (unitHasCompleteRecord({
          learningRecords, module, id: currentUnit.id, env,
        })
            || currentUnit.id === unitId)) { // If already true, unit has complete record or is current unit just completed
      return true;
    }
    return false;
  };
  const allChildrenComplete = module.units ? module.units.reduce(reducer, true) : true;
  const moduleComplete = hasCompletedRecord({ id: moduleId, env, learningRecords });
  // const moduleRecords = learningRecords[lrsId];

  if (!moduleComplete && allChildrenComplete) {
    // Trigger module AA event
    // triggerModulesStatusChange({ courseOutline, status: AA_EVENT_START, env });

    updatedLearningRecs[lrsId] = {
      urn: generateURN({
        uid: userDetails.userid, verb: LRS_VERB_COMPLETED, lrsId, type: OBJECT_TYPE_MODULE,
      }),
      statements: [
        {
          context: generateLRSContext({
            curatedData: courseOutline, env, tutorialId: moduleId, tutorialData: moduleOutline,
          }),
          verb: LRS_VERB_COMPLETED,
          timestamp: new Date().toUTCString(),
          objectType: OBJECT_TYPE_MODULE,
        },
      ],
    };

    logger.info(`Dispatching Module Complete for: ${moduleId}`);
    dispatch(setLearningRecords({ learningRecords: updatedLearningRecs }));
    dispatch(sendUserData({
      uid: userDetails.userid, verb: LRS_VERB_COMPLETED, curatedData: courseOutline, env, tutorialId: moduleId, type: OBJECT_TYPE_MODULE, appVersion, tutorialData: moduleOutline,
    }));
  }
};

export const sendCourseEvent = ({
  dispatch, courseOutline, userDetails, env, verb, appVersion,
}) => {
  const learningRecords = {};
  const lrsId = generateLRSID({ id: courseOutline.id, env });
  learningRecords[lrsId] = {
    urn: generateURN({
      uid: userDetails.userid, verb, lrsId, type: OBJECT_TYPE_COURSE,
    }),
    statements: [
      {
        context: generateLRSContext({ curatedData: courseOutline, env }),
        verb,
        timestamp: new Date().toUTCString(),
        objectType: OBJECT_TYPE_COURSE,
      },
    ],
  };

  dispatch(setLearningRecords({ learningRecords }));
  dispatch(sendUserData({
    uid: userDetails.userid, verb, curatedData: courseOutline, env, type: OBJECT_TYPE_COURSE, appVersion,
  }));
};
